import {
  optionalMutationBody,
  optionalQueryParams,
} from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2AssistantConversation,
  AdminV2AssistantConversationsResponse,
  AdminV2AssistantMessagesResponse,
  AdminV2AssistantSendMessageResponse,
  AdminV2AssistantSuggestionsResponse,
  JsonValue as AdminV2JsonValue,
} from "@/types/adminV2";

/**
 * Admin V2 assistant endpoint definitions extracted from `baseApi.ts`.
 *
 * @remarks
 * Keeps generated hooks and cache-tag ids on the shared `apiSlice` while moving
 * assistant-specific request ownership out of the central API file.
 *
 * @see docs/admin/README.md — admin surface and API integration ownership
 * @see apps/admin/src/components/assistant/__tests__/AdminAiAssistantWidget.test.tsx
 */
export const buildAdminV2AssistantEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2AssistantSuggestions: builder.query<
    AdminV2AssistantSuggestionsResponse,
    void
  >({
    query: () => ({
      url: "/admin/v2/assistant/suggestions",
    }),
    providesTags: [{ type: "AdminV2Assistant", id: "SUGGESTIONS" }],
    keepUnusedDataFor: 300,
  }),

  getAdminV2AssistantConversations: builder.query<
    AdminV2AssistantConversationsResponse,
    { limit?: number; cursor?: string } | void
  >({
    query: (params) => ({
      url: "/admin/v2/assistant/conversations",
      params: optionalQueryParams(params),
    }),
    providesTags: (result) => {
      if (
        !Array.isArray(result?.conversations) ||
        result.conversations.length === 0
      ) {
        return [{ type: "AdminV2Assistant", id: "CONVERSATIONS" }];
      }

      return [
        { type: "AdminV2Assistant", id: "CONVERSATIONS" },
        ...result.conversations.map((conversation) => ({
          type: "AdminV2Assistant" as const,
          id: conversation.id,
        })),
      ];
    },
    keepUnusedDataFor: 10,
  }),

  createAdminV2AssistantConversation: builder.mutation<
    { conversation: AdminV2AssistantConversation },
    { title?: string } | void
  >({
    query: (body) => ({
      url: "/admin/v2/assistant/conversations",
      method: "POST",
      data: optionalMutationBody(body),
    }),
    invalidatesTags: [{ type: "AdminV2Assistant", id: "CONVERSATIONS" }],
  }),

  getAdminV2AssistantMessages: builder.query<
    AdminV2AssistantMessagesResponse,
    { conversationId: string; limit?: number; cursor?: string }
  >({
    query: ({ conversationId, limit, cursor }) => ({
      url: `/admin/v2/assistant/conversations/${conversationId}/messages`,
      params: {
        ...(typeof limit === "number" ? { limit } : {}),
        ...(cursor ? { cursor } : {}),
      },
    }),
    providesTags: (_result, _error, { conversationId }) => [
      { type: "AdminV2Assistant", id: `MESSAGES_${conversationId}` },
    ],
    keepUnusedDataFor: 5,
  }),

  sendAdminV2AssistantMessage: builder.mutation<
    AdminV2AssistantSendMessageResponse,
    {
      conversationId: string;
      message: string;
      currentPath?: string;
      clientContext?: AdminV2JsonValue;
    }
  >({
    query: ({ conversationId, ...payload }) => ({
      url: `/admin/v2/assistant/conversations/${conversationId}/messages`,
      method: "POST",
      data: payload,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      { type: "AdminV2Assistant", id: "CONVERSATIONS" },
      { type: "AdminV2Assistant", id: `MESSAGES_${conversationId}` },
    ],
  }),

  deleteAdminV2AssistantConversation: builder.mutation<
    { deleted: boolean },
    { conversationId: string }
  >({
    query: ({ conversationId }) => ({
      url: `/admin/v2/assistant/conversations/${conversationId}`,
      method: "DELETE",
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      { type: "AdminV2Assistant", id: "CONVERSATIONS" },
      { type: "AdminV2Assistant", id: `MESSAGES_${conversationId}` },
    ],
  }),
});
